import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';

import { NavigationDrawer, MainAppBar } from '../components/index.js';
import { isCareInformationDrawerOpen } from '../redux/selectors.js';


const rightDrawerWidth = 360;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Main = styled('main', { shouldForwardProp: (prop) => !['open', 'noPadding'].includes(prop as string) })<{
  open?: boolean;
  noPadding?: boolean;
}>(({ theme, open, noPadding }) => ({
  flexGrow: 1,
  /**
   * `clip` rather than `hidden`: `hidden` makes this element a scroll container, which becomes the
   * scrollport for any `position: sticky` descendant. Since the window scrolls and this element does
   * not, sticky children would never stick. `clip` clips the drawer transition without that effect.
   */
  overflowX: 'clip',
  padding: noPadding ? 0 : theme.spacing(3, 4),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: rightDrawerWidth,
  }),
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: 'relative',
}));

const Layout = () => {
  const [navigationDrawerOpen, setNavigationDrawerOpen] = React.useState(false);
  const careInformationDrawerOpen = useSelector(isCareInformationDrawerOpen);

  const { patientId } = useParams();
  const { pathname } = useLocation();


  return (
    <Box sx={{ display: 'flex' }}>
      <MainAppBar
        leftOpen={navigationDrawerOpen}
        rightOpen={careInformationDrawerOpen}
        open={navigationDrawerOpen}
        setOpen={setNavigationDrawerOpen}
      />
      <NavigationDrawer open={navigationDrawerOpen} setOpen={setNavigationDrawerOpen} />
      <Main open={careInformationDrawerOpen} noPadding={[`/patients/${patientId}`, '/streams'].includes(pathname)}>
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
};

export default Layout;
